import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InstallAppButton } from "@/components/InstallAppButton";
import {
  Boxes, Users, Truck, Scale, Sun, Moon, LogOut, Layers, FolderTree, UserCog, GitCompare,
  ScanText, CreditCard, Bot, Building2, Clock, ShieldAlert, ShieldCheck, UserCircle,
  Database, FolderKanban, ChevronDown, GraduationCap, ClipboardList, LayoutDashboard,
} from "lucide-react";
import DashboardView from "@/components/DashboardView";
import InsumosView from "@/components/InsumosView";
import CuadrillasView from "@/components/CuadrillasView";
import EquiposView from "@/components/EquiposView";
import FasarView from "@/components/FasarView";
import APUView from "@/components/APUView";
import PresupuestoView from "@/components/PresupuestoView";
import UsersView from "@/components/UsersView";
import ComparadorView from "@/components/ComparadorView";
import LicitacionesView from "@/components/LicitacionesView";
import EstimacionesView from "@/components/EstimacionesView";
import SubscriptionView from "@/components/SubscriptionView";
import SettingsView from "@/components/SettingsView";
import EmpresasView from "@/components/EmpresasView";
import SecurityView from "@/components/SecurityView";
import AccountView from "@/components/AccountView";
import TutorialView from "@/components/TutorialView";

const GROUPS = [
  {
    title: "Catálogos", icon: Database,
    items: [
      { key: "insumos", label: "Insumos", icon: Boxes },
      { key: "cuadrillas", label: "Cuadrillas", icon: Users },
      { key: "equipos", label: "Equipos", icon: Truck },
      { key: "fasar", label: "FASAR", icon: Scale },
    ],
  },
  {
    title: "Proyecto", icon: FolderKanban,
    items: [
      { key: "apu", label: "Análisis APU", icon: Layers },
      { key: "presupuesto", label: "Presupuesto", icon: FolderTree },
      { key: "estimaciones", label: "Estimaciones", icon: ClipboardList },
      { key: "comparar", label: "Comparar", icon: GitCompare },
      { key: "licitaciones", label: "Licitaciones IA", icon: ScanText },
    ],
  },
  {
    title: "Administración", icon: ShieldCheck,
    items: [
      { key: "usuarios", label: "Usuarios", icon: UserCog },
      { key: "seguridad", label: "Seguridad", icon: ShieldCheck },
      { key: "suscripcion", label: "Suscripción", icon: CreditCard },
      { key: "ajustes", label: "IA & Ajustes", icon: Bot },
    ],
  },
  {
    title: "Plataforma", icon: Building2,
    items: [{ key: "empresas", label: "Empresas", icon: Building2 }],
  },
];

const ROLE_LABEL = { admin: "Administrador", presupuestista: "Presupuestista", consulta: "Consulta" };

const VIEWS = {
  insumos: InsumosView, cuadrillas: CuadrillasView, equipos: EquiposView, fasar: FasarView,
  apu: APUView, presupuesto: PresupuestoView, estimaciones: EstimacionesView, comparar: ComparadorView, licitaciones: LicitacionesView,
  usuarios: UsersView, suscripcion: SubscriptionView, ajustes: SettingsView, empresas: EmpresasView,
  seguridad: SecurityView, cuenta: AccountView, tutorial: TutorialView, inicio: DashboardView,
};

export default function Workspace() {
  const { user, company, access, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [active, setActive] = useState("inicio");
  const [openId, setOpenId] = useState(null);
  const go = (key, id = null) => { setOpenId(id); setActive(key); };

  const allowed = new Set(user?.sections || []);
  const groups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => allowed.has(it.key) && (it.key !== "empresas" || user?.superadmin)) }))
    .filter((g) => g.items.length > 0);
  const allItems = groups.flatMap((g) => g.items);
  const activeItem = allItems.find((i) => i.key === active);
  const ActiveView = VIEWS[active];

  const GroupTab = ({ g }) => {
    const groupActive = g.items.some((i) => i.key === active);
    const GIcon = g.icon;
    const base = "shrink-0 inline-flex items-center gap-1.5 h-11 px-3.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50";
    const on = "text-amber-400 border-amber-500 bg-amber-500/10";
    const off = "text-slate-300 hover:text-white hover:bg-white/5 border-transparent";

    if (g.items.length === 1) {
      const it = g.items[0];
      return (
        <button data-testid={`nav-${it.key}`} onClick={() => setActive(it.key)} className={`${base} ${groupActive ? on : off}`}>
          <GIcon className="h-4 w-4" /> {it.label}
        </button>
      );
    }
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={`nav-group-${g.title.toLowerCase()}`} className={`${base} ${groupActive ? on : off}`}>
            <GIcon className="h-4 w-4" />
            {groupActive ? <span>{g.title} · <span className="text-amber-300">{activeItem?.label}</span></span> : g.title}
            <ChevronDown className="h-3.5 w-3.5 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-52 border-slate-700/80 bg-[#0E172A] text-slate-200 shadow-2xl">
          {g.items.map((it) => {
            const Icon = it.icon;
            const on2 = active === it.key;
            return (
              <DropdownMenuItem
                key={it.key}
                data-testid={`nav-${it.key}`}
                onClick={() => setActive(it.key)}
                className={`gap-2 text-xs cursor-pointer focus:bg-amber-500/20 focus:text-amber-200 ${on2 ? "text-amber-300 bg-amber-500/10" : "text-slate-200"}`}
              >
                <Icon className="h-4 w-4" /> {it.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Fila 1 — Barra de marca / utilidades */}
      <header className="min-h-14 shrink-0 bg-[#0A1224] text-white flex items-center justify-between px-3 sm:px-4 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/brand/emauran.png" alt="EMAURAN by OOMINING" className="h-12 w-auto object-contain [filter:brightness(0)_invert(1)]" data-testid="header-logo" />
          {company?.name && (
            <span className="hidden sm:inline-flex items-center rounded-sm border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-[11px] text-slate-200 truncate max-w-[220px]">
              {company.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <InstallAppButton />
          <button
            data-testid="account-btn"
            onClick={() => setActive("cuenta")}
            className={`hidden sm:flex items-center gap-2 rounded-sm px-2 h-8 transition-colors ${active === "cuenta" ? "bg-amber-500/15 text-amber-300" : "text-slate-200 hover:bg-white/5"}`}
            title="Mi cuenta"
          >
            <UserCircle className="h-5 w-5" />
            <span className="text-left leading-tight">
              <span className="block text-xs font-medium">{user?.name || user?.email}</span>
              <span className="block text-[10px] text-amber-400/90 uppercase tracking-wider">{ROLE_LABEL[user?.role] || user?.role}</span>
            </span>
          </button>
          <Button data-testid="account-btn-icon" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-slate-200 hover:bg-white/10 hover:text-white sm:hidden" onClick={() => setActive("cuenta")}>
            <UserCircle className="h-4 w-4" />
          </Button>
          <Button data-testid="theme-toggle" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-slate-200 hover:bg-white/10 hover:text-white" onClick={toggle}>
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button data-testid="logout-btn" variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-slate-200 hover:bg-red-500/20 hover:text-red-300" onClick={logout}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Fila 2 — Pestañas de navegación superiores */}
      <nav data-testid="top-nav-bar" className="h-11 shrink-0 bg-[#0E172A] border-b border-slate-800 flex items-center px-2 sm:px-3 gap-0.5 overflow-x-auto no-scrollbar">
        <button
          data-testid="nav-inicio"
          onClick={() => setActive("inicio")}
          className={`shrink-0 inline-flex items-center gap-1.5 h-11 px-3.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 ${active === "inicio" ? "text-amber-400 border-amber-500 bg-amber-500/10" : "text-slate-300 hover:text-white hover:bg-white/5 border-transparent"}`}
        >
          <LayoutDashboard className="h-4 w-4" /> Inicio
        </button>
        {groups.map((g) => <GroupTab key={g.title} g={g} />)}
        <button
          data-testid="nav-tutorial"
          onClick={() => setActive("tutorial")}
          className={`shrink-0 inline-flex items-center gap-1.5 h-11 px-3.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 ${active === "tutorial" ? "text-amber-400 border-amber-500 bg-amber-500/10" : "text-slate-300 hover:text-white hover:bg-white/5 border-transparent"}`}
        >
          <GraduationCap className="h-4 w-4" /> Tutorial
        </button>
        <div className="flex-1 min-w-[8px]" />
        <span className="font-mono-num text-[10px] text-slate-500 pr-2 hidden md:inline">EMAURAN v1.3</span>
      </nav>

      {/* Contenido */}
      <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden">
        {access && !access.active && (
          <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-destructive/10 border-b border-destructive/30 text-destructive text-xs" data-testid="access-banner-blocked">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <span>Modo lectura: sin suscripción activa. Crear/editar presupuestos, exportar y la IA están bloqueados.</span>
            {user?.role === "admin" && <Button size="sm" variant="outline" className="h-6 ml-auto rounded-sm text-xs" onClick={() => setActive("suscripcion")} data-testid="banner-subscribe-btn">Ver suscripción</Button>}
          </div>
        )}
        {access && access.active && access.reason === "trial" && (
          <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-amber-500/10 border-b border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs" data-testid="access-banner-trial">
            <Clock className="h-4 w-4 shrink-0" />
            <span>Periodo de prueba: {access.days_left} días restantes.</span>
            {user?.role === "admin" && <Button size="sm" variant="outline" className="h-6 ml-auto rounded-sm text-xs" onClick={() => setActive("suscripcion")} data-testid="banner-trial-subscribe-btn">Suscribirme</Button>}
          </div>
        )}
        {ActiveView ? <ActiveView key={active} go={go} openId={openId} /> : <div className="p-10 text-muted-foreground text-sm">Selecciona una sección.</div>}
      </main>
    </div>
  );
}
